"use client";

import {
	domMin,
	LazyMotion,
	m,
	useAnimation,
	useReducedMotion,
	type Variants,
} from "framer-motion";
import {
	forwardRef,
	type HTMLAttributes,
	type MouseEvent,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
} from "react";
import { cn } from "@/lib/utils";

interface AnimatedIconProps
	extends Omit<
		HTMLAttributes<HTMLDivElement>,
		| "color"
		| "onDrag"
		| "onDragStart"
		| "onDragEnd"
		| "onAnimationStart"
		| "onAnimationEnd"
		| "onAnimationIteration"
	> {
	size?: number;
	duration?: number;
	isAnimated?: boolean;
	color?: string;
}

export interface EyeIconHandle {
	startAnimation: () => void;
	stopAnimation: () => void;
}

export const EyeIcon = forwardRef<EyeIconHandle, AnimatedIconProps>(
	function EyeIcon(
		{
			onMouseEnter,
			onMouseLeave,
			className,
			size = 24,
			duration = 1,
			isAnimated = true,
			color,
			...props
		},
		ref,
	) {
		const eyeControls = useAnimation();
		const pupilControls = useAnimation();
		const reduced = useReducedMotion();
		const isControlled = ref !== null && ref !== undefined;
		const hasPlayedOnLoad = useRef(false);

		useEffect(() => {
			if (hasPlayedOnLoad.current || !isAnimated || reduced || isControlled)
				return;

			hasPlayedOnLoad.current = true;
			eyeControls.start("blink");
			pupilControls.start("scan");
		}, [eyeControls, isAnimated, isControlled, pupilControls, reduced]);

		useImperativeHandle(ref, () => {
			return {
				startAnimation: () => {
					if (reduced) {
						eyeControls.start("open");
						pupilControls.start("center");
					} else {
						eyeControls.start("blink");
						pupilControls.start("scan");
					}
				},
				stopAnimation: () => {
					eyeControls.start("open");
					pupilControls.start("center");
				},
			};
		}, [eyeControls, pupilControls, reduced]);

		const handleEnter = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				onMouseEnter?.(event);
				if (!isAnimated || reduced || isControlled) return;
				eyeControls.start("blink");
				pupilControls.start("scan");
			},
			[
				eyeControls,
				isAnimated,
				isControlled,
				onMouseEnter,
				pupilControls,
				reduced,
			],
		);

		const handleLeave = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				onMouseLeave?.(event);
				if (isControlled) return;
				eyeControls.start("open");
				pupilControls.start("center");
			},
			[eyeControls, isControlled, onMouseLeave, pupilControls],
		);

		const eyeVariants: Variants = {
			open: { scaleY: 1 },
			blink: {
				scaleY: [1, 0.1, 1],
				transition: {
					duration: 0.25 * duration,
					repeatDelay: 2.4,
					ease: "easeInOut",
				},
			},
		};
		const pupilVariants: Variants = {
			center: { x: 0 },
			scan: {
				x: [-2, 2, -1, 1, 0],
				transition: {
					duration: 1.6 * duration,
					ease: "easeInOut",
				},
			},
		};

		return (
			<LazyMotion features={domMin} strict>
				<m.div
					className={cn("inline-flex items-center justify-center", className)}
					onMouseEnter={handleEnter}
					onMouseLeave={handleLeave}
					{...props}
					style={{ color, ...props.style }}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width={size}
						height={size}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<title>Views</title>
						<m.path
							d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"
							animate={eyeControls}
							initial="open"
							variants={eyeVariants}
							style={{
								transformBox: "fill-box",
								transformOrigin: "center",
							}}
						/>
						<m.circle
							cx="12"
							cy="12"
							r="3"
							animate={pupilControls}
							initial="center"
							variants={pupilVariants}
						/>
					</svg>
				</m.div>
			</LazyMotion>
		);
	},
);

export interface TrendingUpIconHandle {
	startAnimation: () => void;
	stopAnimation: () => void;
}

export const TrendingUpIcon = forwardRef<
	TrendingUpIconHandle,
	AnimatedIconProps
>(function TrendingUpIcon(
	{
		onMouseEnter,
		onMouseLeave,
		className,
		size = 24,
		duration = 1,
		isAnimated = true,
		color,
		...props
	},
	ref,
) {
	const controls = useAnimation();
	const reduced = useReducedMotion();
	const isControlled = ref !== null && ref !== undefined;
	const hasPlayedOnLoad = useRef(false);

	useEffect(() => {
		if (hasPlayedOnLoad.current || !isAnimated || reduced || isControlled)
			return;

		hasPlayedOnLoad.current = true;
		controls.start("animate");
	}, [controls, isAnimated, isControlled, reduced]);

	useImperativeHandle(ref, () => {
		return {
			startAnimation: () =>
				reduced ? controls.start("normal") : controls.start("animate"),
			stopAnimation: () => controls.start("normal"),
		};
	}, [controls, reduced]);

	const handleEnter = useCallback(
		(event: MouseEvent<HTMLDivElement>) => {
			onMouseEnter?.(event);
			if (!isAnimated || reduced || isControlled) return;
			controls.start("animate");
		},
		[controls, isAnimated, isControlled, onMouseEnter, reduced],
	);

	const handleLeave = useCallback(
		(event: MouseEvent<HTMLDivElement>) => {
			onMouseLeave?.(event);
			if (!isControlled) controls.start("normal");
		},
		[controls, isControlled, onMouseLeave],
	);

	const iconVariants: Variants = {
		normal: { scale: 1, rotate: 0 },
		animate: {
			scale: [1, 1.06, 0.98, 1],
			rotate: [0, 3, -2, 0],
			transition: {
				duration: 0.9 * duration,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};
	const arrowVariants: Variants = {
		normal: { opacity: 1, x: 0, y: 0, rotate: 0 },
		animate: {
			opacity: [0, 1],
			x: [-4, 0],
			y: [4, 0],
			rotate: [8, 0],
			transition: {
				duration: 0.55 * duration,
				ease: [0.16, 1, 0.3, 1],
				delay: 0.12,
			},
		},
	};
	const pathVariants: Variants = {
		normal: { pathLength: 1, pathOffset: 0 },
		animate: {
			pathLength: [0, 1],
			pathOffset: [1, 0],
			transition: {
				duration: 0.75 * duration,
				ease: "easeInOut",
			},
		},
	};

	return (
		<LazyMotion features={domMin} strict>
			<m.div
				className={cn("inline-flex items-center justify-center", className)}
				onMouseEnter={handleEnter}
				onMouseLeave={handleLeave}
				{...props}
				style={{ color, ...props.style }}
			>
				<m.svg
					xmlns="http://www.w3.org/2000/svg"
					width={size}
					height={size}
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth={2}
					strokeLinecap="round"
					strokeLinejoin="round"
					animate={controls}
					initial="normal"
					variants={iconVariants}
				>
					<title>Growing views</title>
					<m.path
						d="m22 7-8.5 8.5-5-5L2 17"
						variants={pathVariants}
						initial="normal"
						animate={controls}
					/>
					<m.path
						d="M16 7h6v6"
						variants={arrowVariants}
						initial="normal"
						animate={controls}
					/>
				</m.svg>
			</m.div>
		</LazyMotion>
	);
});

export interface RocketIconHandle {
	startAnimation: () => void;
	stopAnimation: () => void;
}

export const RocketIcon = forwardRef<RocketIconHandle, AnimatedIconProps>(
	function RocketIcon(
		{
			onMouseEnter,
			onMouseLeave,
			className,
			size = 24,
			duration = 1,
			isAnimated = true,
			color,
			...props
		},
		ref,
	) {
		const controls = useAnimation();
		const reduced = useReducedMotion();
		const isControlled = ref !== null && ref !== undefined;
		const hasPlayedOnLoad = useRef(false);

		useEffect(() => {
			if (hasPlayedOnLoad.current || !isAnimated || reduced || isControlled)
				return;

			hasPlayedOnLoad.current = true;
			controls.start("animate");
		}, [controls, isAnimated, isControlled, reduced]);

		useImperativeHandle(ref, () => {
			return {
				startAnimation: () =>
					reduced ? controls.start("normal") : controls.start("animate"),
				stopAnimation: () => controls.start("normal"),
			};
		}, [controls, reduced]);

		const handleEnter = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				onMouseEnter?.(event);
				if (!isAnimated || reduced || isControlled) return;
				controls.start("animate");
			},
			[controls, isAnimated, isControlled, onMouseEnter, reduced],
		);

		const handleLeave = useCallback(
			(event: MouseEvent<HTMLDivElement>) => {
				onMouseLeave?.(event);
				if (!isControlled) controls.start("normal");
			},
			[controls, isControlled, onMouseLeave],
		);

		const rocketVariants: Variants = {
			normal: { y: 0, x: 0 },
			animate: {
				y: [0, -3, 0],
				x: [0, 3, 0],
				transition: {
					duration: 0.7 * duration,
					ease: "easeInOut",
				},
			},
		};
		const thrustVariants: Variants = {
			normal: { opacity: 1, scale: 1 },
			animate: {
				opacity: [1, 0.3, 1],
				scale: [1, 1.4, 1],
				transition: {
					duration: 0.4 * duration,
					ease: "easeInOut",
				},
			},
		};

		return (
			<LazyMotion features={domMin} strict>
				<m.div
					className={cn("inline-flex items-center justify-center", className)}
					onMouseEnter={handleEnter}
					onMouseLeave={handleLeave}
					{...props}
					style={{ color, ...props.style }}
				>
					<m.svg
						xmlns="http://www.w3.org/2000/svg"
						width={size}
						height={size}
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<title>Maximum reach</title>
						<m.path
							d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
							variants={rocketVariants}
							initial="normal"
							animate={controls}
							style={{
								transformBox: "fill-box",
								transformOrigin: "center",
							}}
						/>
						<m.path
							d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"
							variants={rocketVariants}
							initial="normal"
							animate={controls}
						/>
						<m.path
							d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"
							variants={rocketVariants}
							initial="normal"
							animate={controls}
						/>
						<m.path
							d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
							variants={thrustVariants}
							initial="normal"
							animate={controls}
						/>
					</m.svg>
				</m.div>
			</LazyMotion>
		);
	},
);
